import {
  useState,
  useEffect,
  KeyboardEvent,
  ChangeEvent,
  useMemo,
  useRef,
} from "react";
import { v4 as uuidv4 } from "uuid";
import useUser from "../../hooks/user";
import { getTextLanguage, DisplayMessage } from "../../types";
import { useParams } from "react-router-dom";
import classNames from "./Chat.module.css";
import { useAxios } from "../../hooks/axios";
import api, { SendMessageRequest } from "../../hooks/api-new";
import { useRoom } from "../context/room/room";
import { IoClose } from "react-icons/io5";
import Button from "./Button";
import { useChatMessages } from "../../hooks/chat";
import Linkify from 'linkify-react';

const Chat = ({
  canAskQuestions,
  mode,
  onOpenChange,
  onReceivedMessages,
  className,
}: {
  canAskQuestions: boolean;
  mode: "chat" | "qna";
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onReceivedMessages?: (numMessages: number) => void;
  className?: string;
}) => {
  const [messages, setMessages] = useChatMessages();
  const [input, setInput] = useState("");
  const room = useRoom();
  const roomLanguages = useMemo<string[]>(() => {
    if (!room?.room) {
      return [];
    }
    return room.room.languages;
  }, [room]);
  const [user] = useUser();
  const params = useParams();

  const axios = useAxios();
  const publishMessage = api.usePublishMessage(axios);

  // Function to handle sending messages
  const sendMessage = async () => {
    if (input.trim() === "") return;

    const messageId = uuidv4();
    const sourceLanguage =
      getTextLanguage(user.metadata.selectedLanguage) || "en";
    const targetLanguages = roomLanguages
      .filter((lang) => lang !== sourceLanguage)
      .join(",");

    const roomId = params?.id;
    if (!roomId) {
      return;
    }

    // Create optimistic message
    const optimisticMessage: DisplayMessage = {
      messageId,
      text: { [sourceLanguage]: input },
      name: user.metadata.name ?? "Guest",
      identity: user.livekitId,
      pending: true, // Mark as pending
      timestamp: new Date().toISOString(),
    };

    // Add optimistic message immediately
    setMessages((prev) => [...prev, optimisticMessage]);
    setInput("");

    const newMessage: SendMessageRequest = {
      roomId: roomId,
      messageId,
      sourceLanguage,
      targetLanguages,
      text: input,
      name: user.metadata.name ?? "Guest",
      identity: user.livekitId,
    };

    try {
      await publishMessage(newMessage);
    } catch (error) {
      // Remove the optimistic message on error
      setMessages((prev) => prev.filter((msg) => msg.messageId !== messageId));
      console.error("Error sending message:", error);
      throw error;
    }
  };

  // Function to handle input change
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  // Function to handle Enter key press
  const handleEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault(); // Prevents default behavior (like inserting a newline)
      sendMessage();
    }
  };

  const numPreviousMessages = useRef(0);
  useEffect(() => {
    const numNewMessages = Math.max(
      messages.length - numPreviousMessages.current,
      0
    );
    numPreviousMessages.current = messages.length;
    if (onReceivedMessages && numNewMessages > 0) {
      onReceivedMessages(numNewMessages);
    }
  }, [messages.length, onReceivedMessages]);

  const title = useMemo(() => {
    switch (mode) {
      case "chat":
        return "Chat";
      case "qna":
        return "Q&A";
    }
  }, [mode]);

  const messagesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    messagesRef.current?.scrollTo({
      top: messagesRef.current.scrollHeight,
    });
  }, [messages, messagesRef]);

  return (
    <div className={`${classNames.Chat} ${className || ""}`}>
      <div className={classNames.Container}>
        <div className={classNames.Header}>
          <h3>{title}</h3>
          <button onClick={() => onOpenChange && onOpenChange(false)}>
            <IoClose />
          </button>
        </div>
        <div className={classNames.Messages} ref={messagesRef}>
          {messages.map((msg: DisplayMessage) => (
            <div
              key={msg.messageId}
              className={
                // Only show as message received for Q&A mode
                msg.identity === user.livekitId && mode === "chat"
                  ? classNames.MessageSent
                  : classNames.MessageReceived
              }
            >
              <h3>{msg.name}</h3>
              <div className={classNames.MessageContent}>
                {/* Use Linkify for hyperlinks sent in chat */}
                <Linkify options={{ target: "_blank" }}>
                  {user.metadata.selectedLanguage === "autodetect"
                    ? msg.text.en || "    "
                    : msg.text[
                    getTextLanguage(user.metadata.selectedLanguage) || "en"
                    ] ||
                    msg.text.en || // Fallbacks
                    "   "}
                </Linkify>
              </div>
            </div>
          ))}
        </div>
        {canAskQuestions && (
          <div className={classNames.InputArea}>
            <input
              type="text"
              placeholder="Type your questions here"
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleEnter}
              className={classNames.Input}
            />
            <Button size="x-small" onClick={sendMessage}>
              Send
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
export default Chat;
